/**
 * @fileoverview Componente dropdown asíncrono optimizado para selección de opciones con carga dinámica de datos
 * @module DropdownAsyncMC
 * @category Component - Molecule
 * @since 1.0.0
 * @version 3.1.0
 */

import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import SearchComponent from './SearchMC';
import IconMC from './IconMC';
import useClickOutside from '../../hooks/useClickOutside';

/**
 * @typedef {Object} IOptionProps
 * @description Estructura de datos para las opciones del dropdown asíncrono
 * @property {string|number} id - Identificador único de la opción (utilizado como valor en el dropdown)
 * @property {string} label - Texto a mostrar en la lista desplegable para esta opción
 * @property {React.ReactNode} [icon] - Icono opcional a mostrar junto a la opción
 * @property {string} [color] - Color opcional a mostrar junto a la opción (formato #AAAAAA)
 */

/**
 * @typedef {Object} LoadOptionsParams
 * @description Parámetros para la función de carga de opciones
 * @property {string} text - Texto de búsqueda para filtrar opciones
 */

/**
 * @typedef {Object} LoadOptionsResponse
 * @description Respuesta de la función de carga de opciones
 * @property {IOptionProps[]} data - Array de opciones cargadas
 */

/**
 * Componente Molecule de dropdown asíncrono con búsqueda y selección de opciones.
 * 
 * Implementa un dropdown con carga asíncrona de datos, búsqueda en tiempo real con debounce,
 * integración con react-hook-form y renderizado personalizable de opciones.
 * 
 * @component DropdownAsyncMC
 * @category Component - Molecule
 * 
 * @description
 * **Arquitectura Optimizada (v3.1.0):**
 * - ✅ **Performance**: React.memo, useCallback, useMemo para optimización de renderizado
 * - ✅ **Async Loading**: Carga asíncrona con debounce y estados de loading/error
 * - ✅ **Form Integration**: Integración completa con react-hook-form y validaciones
 * - ✅ **Customizable**: Renderizado personalizable de opciones y valores seleccionados
 * - ✅ **Accessibility**: Soporte para labels flotantes y navegación por teclado
 * - ✅ **Search**: Búsqueda integrada con debounce configurable
 * 
 * **Características:**
 * - Carga asíncrona de opciones con función loadOptions personalizable
 * - Búsqueda en tiempo real con debounce configurable
 * - Estados de loading, error y vacío con mensajes i18n
 * - Renderizado personalizable de opciones y valor seleccionado
 * - Integración completa con react-hook-form
 * - Soporte para labels flotantes y estilos TailwindCSS
 * - Manejo de clicks fuera del componente para cerrar dropdown
 * 
 * **Atomic Design:**
 * - **Nivel**: Molecule
 * - **Composición**: Compuesto por átomos SearchComponent, IconMC y elementos HTML base
 * - **Dependencias**: Utiliza SearchMC e IconMC como átomos fundamentales
 * 
 * **Props Interface:**
 * ```javascript
 * {
 *   label: string,                    // Etiqueta requerida del dropdown
 *   loadOptions: Function,            // Función de carga asíncrona requerida
 *   onSelect?: Function,              // Callback opcional al seleccionar opción
 *   defaultValue?: string|number,     // Valor inicial opcional
 *   name?: string,                    // Nombre del campo para formularios
 *   useFloatingLabel?: boolean,       // Estilo de label flotante
 *   className?: string,               // Clases CSS adicionales
 *   placeholder?: string,             // Texto placeholder opcional
 *   debounceTime?: number,            // Tiempo de debounce en ms
 *   renderOption?: Function,          // Renderizado personalizado de opciones
 *   renderSelected?: Function         // Renderizado personalizado del valor seleccionado
 * }
 * ```
 * 
 * **Flujo de Operación:**
 * 1. **Inicialización**: Configuración de estados y hooks de optimización
 * 2. **Carga de Datos**: Llamada asíncrona a loadOptions con debounce
 * 3. **Renderizado**: Muestra opciones con estado de loading/error/vacío
 * 4. **Selección**: Actualiza estado interno y ejecuta callbacks
 * 5. **Integración**: Sincronización con react-hook-form si está presente
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.label - Etiqueta de texto a mostrar sobre el dropdown
 * @param {Function} props.loadOptions - Función para cargar opciones de forma asíncrona
 * @param {Function} [props.onSelect] - Función callback al seleccionar una opción
 * @param {string|number} [props.defaultValue] - Valor inicial preseleccionado en el dropdown
 * @param {string} [props.name] - Nombre del campo para uso en formularios
 * @param {boolean} [props.useFloatingLabel=false] - Activar estilo de label flotante
 * @param {string} [props.className=''] - Clases CSS adicionales para el dropdown
 * @param {string} [props.placeholder] - Texto placeholder cuando no hay selección
 * @param {number} [props.debounceTime=500] - Tiempo en ms para debounce de búsqueda
 * @param {Function} [props.renderOption] - Función personalizada para renderizar opciones
 * @param {Function} [props.renderSelected] - Función personalizada para renderizar valor seleccionado
 * 
 * @returns {JSX.Element} Elemento JSX del dropdown asíncrono renderizado
 * 
 * @example
 * ```jsx
 * // Uso básico con carga de clientes
 * <DropdownAsyncMC
 *   label="Seleccionar Cliente"
 *   loadOptions={CustomersModelMC.searchCustomers}
 *   onSelect={(customer) => console.log('Cliente seleccionado:', customer)}
 *   placeholder="Buscar cliente..."
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Uso avanzado con react-hook-form y renderizado personalizado
 * <DropdownAsyncMC
 *   name="customer_id"
 *   label="Cliente"
 *   loadOptions={CustomersModelMC.searchCustomers}
 *   defaultValue={formData.customer_id}
 *   useFloatingLabel={true}
 *   debounceTime={300}
 *   renderOption={(option, isSelected) => (
 *     <div className={`tw-flex tw-items-center ${isSelected ? 'tw-font-bold' : ''}`}>
 *       <img src={option.avatar} className="tw-w-6 tw-h-6 tw-rounded-full tw-mr-2" />
 *       <span>{option.label}</span>
 *     </div>
 *   )}
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Manejo de errores en carga de opciones
 * <DropdownAsyncMC
 *   label="Productos"
 *   loadOptions={async ({ text }) => {
 *     try {
 *       const response = await ProductsAPI.search(text);
 *       return { data: response.products };
 *     } catch (error) {
 *       throw new Error('Error al cargar productos');
 *     }
 *   }}
 *   onSelect={(product) => setSelectedProduct(product)}
 * />
 * ```
 * 
 * @see {@link SearchMC} Componente de búsqueda integrada
 * @see {@link IconMC} Componente de iconos utilizado
 * @see {@link useClickOutside} Hook para manejo de clicks externos
 * 
 * @since 1.0.0
 * @version 3.1.0 - Optimización completa con React.memo y hooks memoizados
 */

const DropdownAsyncMC = memo(({
  label,
  onSelect,
  defaultValue,
  name,
  loadOptions,
  placeholder,
  className = '',
  buttonClassName: customButtonClassName,
  useFloatingLabel = false,
  debounceTime = 500,
  renderOption,
  renderSelected
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState(defaultValue || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const controlForm = useFormContext();

  const domNode = useClickOutside(() => {
    setIsOpen(false);
  });

  /**
   * Función asíncrona para cargar opciones desde la API con manejo de estados.
   * 
   * Ejecuta la función loadOptions proporcionada como prop, gestiona los estados
   * de loading, error y actualiza las opciones filtradas con la respuesta.
   * 
   * @async
   * @function fetchOptions
   * @memberof DropdownAsyncMC
   * @private
   * 
   * @description
   * **Flujo de Operación:**
   * 1. **Loading State**: Activa estado de carga y limpia errores previos
   * 2. **API Call**: Ejecuta loadOptions con parámetro de búsqueda
   * 3. **Data Update**: Actualiza filteredOptions con response.data
   * 4. **Error Handling**: Captura y establece mensaje de error si falla
   * 5. **Cleanup**: Desactiva loading state en finally
   * 
   * **Estructura de Respuesta Esperada:**
   * ```javascript
   * {
   *   data: [
   *     { id: 1, label: "Opción 1", icon?: "icon-class", color?: "#FF0000" },
   *     { id: 2, label: "Opción 2", icon?: "icon-class", color?: "#00FF00" }
   *   ]
   * }
   * ```
   * 
   * @throws {Error} Cuando la función loadOptions falla o retorna formato incorrecto
   * 
   * @returns {Promise<void>} Promesa que resuelve cuando se completa la carga de opciones
   * 
   * @example
   * ```javascript
   * // La función se ejecuta automáticamente con debounce
   * // cuando searchTerm cambia
   * await fetchOptions(); // Carga opciones con searchTerm actual
   * ```
   */
  const fetchOptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await loadOptions({ text: searchTerm });
      setFilteredOptions(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loadOptions, searchTerm]);

  useEffect(() => {
    const handler = setTimeout(fetchOptions, debounceTime);

    return () => {
      clearTimeout(handler);
    };
  }, [fetchOptions, debounceTime]);

  useEffect(() => {
    // Si defaultValue existe, buscar la opción correspondiente
    if (defaultValue) {
      const selectedOption = filteredOptions.find((option) => String(option.id) === String(defaultValue));
      if (selectedOption) {
        setSelectedValue(selectedOption.id);
      }
    } else {
      // Si defaultValue es vacío/null/undefined, limpiar la selección
      setSelectedValue(null);
    }
  }, [defaultValue, filteredOptions]);

  /**
   * Maneja la selección de una opción del dropdown con integración completa de formularios.
   * 
   * Actualiza el estado interno, cierra el dropdown, ejecuta callbacks y sincroniza
   * con react-hook-form si está presente.
   * 
   * @function handleSelect
   * @memberof DropdownAsyncMC
   * @private
   * 
   * @description
   * **Flujo de Operación:**
   * 1. **State Update**: Actualiza selectedValue con option.id
   * 2. **UI Update**: Cierra el dropdown (setIsOpen(false))
   * 3. **Form Integration**: Actualiza field value si react-hook-form está presente
   * 4. **Callback Execution**: Ejecuta onSelect prop si está definido
   * 5. **Validation**: Trigger validación si hay errores en el campo
   * 
   * **Integración con React Hook Form:**
   * - Actualiza automáticamente el valor del campo en el formulario
   * - Ejecuta trigger de validación para limpiar errores
   * - Compatible con Controller de react-hook-form
   * 
   * @param {IOptionProps} option - Opción seleccionada del dropdown
   * @param {Object} field - Objeto field de react-hook-form (opcional)
   * @param {Object} field.field - Campo del formulario con método onChange
   * @param {Function} field.field.onChange - Función para actualizar valor en formulario
   * 
   * @returns {void}
   * 
   * @example
   * ```javascript
   * // Selección manual programática
   * const customerOption = { id: 123, label: "Cliente ABC" };
   * handleSelect(customerOption, field);
   * ```
   * 
   * @example
   * ```javascript
   * // Uso en JSX con click de usuario
   * <li onClick={() => handleSelect(option, field)}>
   *   {option.label}
   * </li>
   * ```
   */
  const handleSelect = useCallback((option, field) => {
    if (option.disabled || option.isDisabled) return;

    const optionId = option.id;
    setSelectedValue(optionId);
    setIsOpen(false);
    field?.field?.onChange(optionId);
    if (onSelect) {
      onSelect(option);
    }
    if (name && controlForm?.formState?.errors[name]) {
      controlForm?.trigger(name);
    }
  }, [onSelect, name, controlForm]);

  const displayedValue = useMemo(() => {
    if (!selectedValue) return placeholder || 'Todos';
    return filteredOptions.find((option) => String(option.id) === String(selectedValue))?.label || placeholder || 'Todos';
  }, [filteredOptions, selectedValue, placeholder]);
  
  const selectedOption = useMemo(() => {
    if (!selectedValue) return null;
    return filteredOptions.find((option) => String(option.id) === String(selectedValue));
  }, [filteredOptions, selectedValue]);

  /**
   * Genera estilos CSS para puntos de color de las opciones del dropdown.
   * 
   * Crea un objeto de estilo con backgroundColor para mostrar indicadores
   * de color junto a las opciones cuando la propiedad color está presente.
   * 
   * @function dotStyle
   * @memberof DropdownAsyncMC
   * @private
   * 
   * @description
   * **Uso:**
   * - Se utiliza para crear indicadores visuales de color en opciones
   * - Compatible con formato de colores hexadecimales (#RRGGBB)
   * - Aplicado a elementos div con clases de círculo pequeño
   * 
   * **Optimización:**
   * - Memoizada con useCallback para evitar recreación innecesaria
   * - Sin dependencias para máxima eficiencia de renderizado
   * 
   * @param {string} color - Color en formato hexadecimal (#AAAAAA)
   * 
   * @returns {Object} Objeto de estilo CSS con backgroundColor
   * 
   * @example
   * ```javascript
   * // Uso interno para crear estilo de color
   * const redStyle = dotStyle('#FF0000');
   * // Retorna: { backgroundColor: '#FF0000' }
   * ```
   * 
   * @example
   * ```jsx
   * // Aplicación en elemento JSX
   * <div
   *   className="tw-w-2 tw-h-2 tw-rounded-full"
   *   style={dotStyle(option.color)}
   * />
   * ```
   */
  const dotStyle = useCallback((color) => ({
    backgroundColor: color
  }), []);

  /**
   * Renderiza el contenido por defecto de una opción en la lista del dropdown.
   * 
   * Proporciona el layout estándar para mostrar opciones con soporte para
   * iconos, indicadores de color y texto con ellipsis para overflow.
   * 
   * @function defaultRenderOption
   * @memberof DropdownAsyncMC
   * @private
   * 
   * @description
   * **Elementos Renderizados:**
   * - **Icono**: Span con clase de icono si option.icon está presente
   * - **Color**: Div circular con color de fondo si option.color está presente  
   * - **Label**: Span con texto y ellipsis para contenido largo
   * 
   * **Layout:**
   * ```
   * [🔸] [⚫] Texto de la opción...
   * icono color  label (truncado)
   * ```
   * 
   * **Optimización:**
   * - Memoizada con useCallback y dependencia en dotStyle
   * - Reutilizable para todas las opciones del dropdown
   * 
   * @param {IOptionProps} option - Objeto de opción con id, label, icon?, color?
   * @param {string|number} option.id - Identificador único de la opción
   * @param {string} option.label - Texto a mostrar para la opción
   * @param {string} [option.icon] - Clase CSS del icono opcional
   * @param {string} [option.color] - Color hexadecimal opcional (#AAAAAA)
   * 
   * @returns {JSX.Element} Fragmento JSX con el contenido renderizado de la opción
   * 
   * @example
   * ```javascript
   * // Opción con todos los elementos
   * const option = {
   *   id: 1,
   *   label: "Cliente Premium",
   *   icon: "fa-star",
   *   color: "#FFD700"
   * };
   * const rendered = defaultRenderOption(option);
   * ```
   * 
   * @example
   * ```javascript
   * // Opción solo con texto
   * const simpleOption = {
   *   id: 2,
   *   label: "Opción simple"
   * };
   * const rendered = defaultRenderOption(simpleOption);
   * ```
   */
  const defaultRenderOption = useCallback((option) => (
    <>
      {option.icon && <span className={`tw-text-xs tw-mr-2 ${option.icon}`} />}
      {option.color && (
        <div
          className="tw-w-2 tw-h-2 tw-block tw-rounded-full tw-mr-2"
          style={dotStyle(option.color)}
        />
      )}
      <span className="tw-overflow-hidden tw-block tw-text-ellipsis tw-w-4/5">
        {option.label}
      </span>
    </>
  ), [dotStyle]);

  /**
   * Renderiza el valor seleccionado en el botón principal del dropdown.
   * 
   * Proporciona el layout por defecto para mostrar la opción seleccionada
   * con soporte para iconos, colores y fallback a displayedValue.
   * 
   * @function defaultRenderSelected
   * @memberof DropdownAsyncMC
   * @private
   * 
   * @description
   * **Elementos Renderizados:**
   * - **Icono**: Elemento i con clase de icono si option?.icon está presente
   * - **Color**: Div circular con color si option?.color está presente
   * - **Label**: Span con texto seleccionado o displayedValue como fallback
   * 
   * **Comportamiento de Fallback:**
   * - Si option?.label existe: muestra option.label
   * - Si no: muestra displayedValue (que puede ser placeholder o 'Todos')
   * - Maneja casos donde option es null/undefined
   * 
   * **Optimización:**
   * - Memoizada con useCallback
   * - Dependencias: dotStyle y displayedValue
   * - Evita re-renders innecesarios del botón principal
   * 
   * @param {IOptionProps|null|undefined} option - Opción seleccionada o null
   * @param {string|number} [option.id] - ID de la opción seleccionada
   * @param {string} [option.label] - Texto de la opción seleccionada
   * @param {string} [option.icon] - Clase CSS del icono opcional
   * @param {string} [option.color] - Color hexadecimal opcional
   * 
   * @returns {JSX.Element} Fragmento JSX con el valor seleccionado renderizado
   * 
   * @example
   * ```javascript
   * // Con opción seleccionada completa
   * const selectedOption = {
   *   id: 5,
   *   label: "Juan Pérez",
   *   icon: "fa-user",
   *   color: "#3B82F6"
   * };
   * const rendered = defaultRenderSelected(selectedOption);
   * ```
   * 
   * @example
   * ```javascript
   * // Sin opción seleccionada (fallback a placeholder)
   * const rendered = defaultRenderSelected(null);
   * // Mostrará displayedValue (placeholder o 'Todos')
   * ```
   */
  const defaultRenderSelected = useCallback((option) => (
    <>
      {option?.icon && <i className={`tw-text-xs tw-mr-2 ${option.icon}`}></i>}
      {option?.color && (
        <div
          className="tw-w-2 tw-h-2 tw-block tw-rounded-full tw-mr-2"
          style={dotStyle(option.color)}
        />
      )}
      <span className="tw-block tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
        {option?.label || displayedValue}
      </span>
    </>
  ), [dotStyle, displayedValue]);

  const buttonClassName = useMemo(() => customButtonClassName || [
    'tw-bg-gray-50',
    'tw-text-default',
    'tw-rounded-xl',
    'tw-w-full',
    'tw-px-4',
    'tw-flex',
    'tw-items-center',
    'tw-justify-between',
    'tw-border',
    'tw-relative',
    useFloatingLabel ? 'tw-pt-5 tw-p-0' : 'tw-py-3'
  ].join(' '), [customButtonClassName, useFloatingLabel]);

  const dropdownClass = useMemo(() => [
    'tw-absolute ',
    'tw-bg-white',
    'tw-border ',
    'tw-rounded-md',
    'tw-shadow-lg',
    'tw-z-50',
    'tw-w-full',
    'tw-mt-1'
  ].join(' '), []);

  const listItemClassName = useMemo(() => [
    'tw-px-4',
    'tw-py-2',
    'hover:tw-bg-blue-100',
    'tw-flex',
    'tw-items-center',
    'tw-cursor-pointer',
    'tw-break-words'
  ].join(' '), []);

  const disabledListItemClassName = useMemo(() => [
    'tw-cursor-not-allowed',
    'tw-opacity-60',
    'hover:tw-bg-white'
  ].join(' '), []);

  const floatingLabelClassNames = useMemo(() => [
    "tw-absolute",
    "tw-text-xs",
    "tw-text-blue-500",
    "tw-top-[5px]",
    "tw-z-10",
    "tw-start-4"
  ].join(" "), []);

  /**
   * Renderiza el contenido completo del dropdown con todos sus elementos internos.
   * 
   * Función principal que construye la interfaz completa del dropdown incluyendo
   * label, botón, lista de opciones, estados de carga y manejo de errores.
   * 
   * @function dropdownContent
   * @memberof DropdownAsyncMC
   * @private
   * 
   * @description
   * **Estructura del Componente:**
   * 1. **Container**: Wrapper principal con clases tw-wrapper
   * 2. **Label**: Label fijo o flotante según useFloatingLabel
   * 3. **Button**: Botón principal con valor seleccionado y flecha
   * 4. **Error Display**: Mensaje de error de validación si existe
   * 5. **Dropdown List**: Lista desplegable con búsqueda y opciones
   * 
   * **Estados Manejados:**
   * - **Loading**: Spinner o mensaje de carga
   * - **Error**: Mensaje de error de API
   * - **Empty**: Mensaje cuando no hay opciones
   * - **Options**: Lista de opciones disponibles
   * 
   * **Integración con Formularios:**
   * - Compatible con react-hook-form a través del parámetro field
   * - Muestra errores de validación automáticamente
   * - Maneja trigger de validación en selección
   * 
   * **Componentes Integrados:**
   * - SearchComponent para búsqueda interna
   * - IconMC para flecha del dropdown
   * - useClickOutside para cerrar al hacer click fuera
   * 
   * @param {Object} [field] - Objeto field de react-hook-form Controller
   * @param {Object} [field.field] - Campo del formulario con métodos de control
   * @param {Function} [field.field.onChange] - Función para actualizar valor
   * 
   * @returns {JSX.Element} Elemento JSX completo del dropdown
   * 
   * @example
   * ```javascript
   * // Uso sin formulario
   * return dropdownContent();
   * ```
   * 
   * @example
   * ```javascript
   * // Uso con react-hook-form Controller
   * <Controller
   *   name="customer_id"
   *   control={control}
   *   render={dropdownContent}
   * />
   * ```
   */
  const dropdownContent = (field) => (
    <div className={`tw-wrapper tw-relative tw-w-full ${className}`} ref={domNode}>
      {!useFloatingLabel && label && (
        <label
          htmlFor={label}
          className="tw-text-zinc-700 tw-mb-1 tw-text-base tw-font-bold"
        >
          {label}
        </label>
      )}
      <button id={label} type="button" onClick={useCallback(() => setIsOpen(!isOpen), [isOpen])} className={buttonClassName}>
        {(useFloatingLabel) && (
          <label htmlFor={label} className={floatingLabelClassNames}>
            {label}
          </label>
        )}
        <span className="tw-flex tw-items-center tw-overflow-hidden tw-w-full">
          {renderSelected ? renderSelected(selectedOption) : defaultRenderSelected(selectedOption)}
        </span>
        <div className="tw-absolute tw-top-[34%] tw-right-4">
          <IconMC
            name={`arrow-ios-${isOpen ? 'up' : 'down'}ward-outline`}
            size="base"
            className="tw-text-gray-600"
          />
        </div>
      </button>
      {name && controlForm?.formState?.errors[name] && (
        <p className="tw-text-xs tw-text-red-500">{controlForm?.formState?.errors[name].message}</p>
      )}
      {isOpen && (
        <div className={dropdownClass}>
          <div className="tw-px-2 tw-pt-2">
            <SearchComponent
              placeholder="Buscar..."
              onChange={setSearchTerm}
              inputClassName="tw-text-sm tw-bg-white"
              debounceTime={200}
            />
          </div>
          <ul className="tw-py-1 tw-overflow-y-auto tw-max-h-60 tw-overflow-x-hidden tw-pl-0">
            {(() => {
              if (loading) {
                return (
                  <li className="tw-px-4 tw-py-2 tw-text-gray-500">
                    {t('dropdown_mc.loading')}
                  </li>
                );
              }
              if (error) {
                return (
                  <li className="tw-px-4 tw-py-2 tw-text-red-500">
                    {t('dropdown_mc.error')}
                  </li>
                );
              }
              if (filteredOptions.length === 0) {
                return (
                  <li className="tw-px-4 tw-py-2 tw-text-gray-500">
                    {t('dropdown_mc.no_options')}
                  </li>
                );
              }
              return filteredOptions.map((option) => {
                const disabled = option.disabled || option.isDisabled;

                return (
                  <li
                    key={option.id}
                    onClick={() => handleSelect(option, field)}
                    aria-disabled={disabled}
                    className={`${listItemClassName} ${selectedValue === option.id ? 'tw-bg-blue-500 tw-text-white' : ''
                    } ${disabled ? disabledListItemClassName : ''}`}
                  >
                    {renderOption ? renderOption(option, selectedValue === option.id) : defaultRenderOption(option)}
                  </li>
                );
              });
            })()}
          </ul>
        </div>
      )}
    </div>
  );

  if (!name || !controlForm?.control) {
    return dropdownContent();
  }

  return (
    <Controller
      name={name}
      control={controlForm.control}
      defaultValue={defaultValue || ''}
      render={dropdownContent}
    />
  );
});

DropdownAsyncMC.displayName = 'DropdownAsyncMC';

export default DropdownAsyncMC;
